import { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { toast } from "sonner";
import { Card, Label, Loading } from "./MeHome";

const LABELS = {
  first_name: "First name", last_name: "Last name", preferred_name: "Preferred name", email: "Email", phone: "Phone", address_line1: "Street address", city: "City",
  province: "Province", postal_code: "Postal code", date_of_birth: "Date of birth", td1_federal_claim: "TD1 federal claim", td1_provincial_claim: "TD1 Ontario claim",
  emergency_contact_name: "Emergency contact", emergency_contact_relationship: "Relationship", emergency_contact_phone: "Emergency phone",
  bank_name: "Bank name", bank_institution: "Institution #", bank_transit: "Transit #", bank_account: "Account #",
};
const CONTACT = ["phone", "address_line1", "city", "postal_code", "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone"];
const inp = "emp-input mt-1.5";
const STATUS_CLS = { pending: "bg-amber-50 text-amber-600", approved: "bg-emerald-50 text-emerald-600", rejected: "bg-rose-50 text-rose-600", applied: "bg-slate-100 text-slate-600" };

export default function MeProfile() {
  const [p, setP] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [contact, setContact] = useState({});
  const [editing, setEditing] = useState(false);
  const [changes, setChanges] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/me/profile"), api.get("/me/requests")]).then(([a, b]) => { setP(a.data); setReqs(b.data); setContact(Object.fromEntries(CONTACT.map((k) => [k, a.data[k] || ""]))); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!p) return <Loading />;

  const saveContact = async () => {
    setBusy(true);
    try { const { data } = await api.put("/me/contact", contact); toast.success(data.changed.length ? "Contact details updated" : "No changes"); setEditing(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const submitRequest = async () => {
    const clean = Object.fromEntries(Object.entries(changes).filter(([, v]) => String(v).trim() !== ""));
    if (!Object.keys(clean).length) return toast.error("Enter at least one new value");
    setBusy(true);
    try { await api.post("/me/requests", { changes: clean, note }); toast.success("Request sent to your employer"); setChanges({}); setNote(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pending = reqs.some((r) => r.status === "pending");

  return (
    <div className="space-y-5" data-testid="me-profile">
      <div><Label>Profile</Label><h1 className="text-4xl font-extrabold tracking-tight mt-2 text-slate-900">{p.first_name} {p.last_name}</h1><p className="text-sm text-slate-500 mt-2">Hired {p.hire_date || "—"} · {p.pay_type} · {p.pay_frequency}</p></div>

      <Card data-testid="me-contact-card">
        <div className="flex items-center justify-between"><Label>Contact details — you can edit these</Label>
          {!editing && <button data-testid="me-contact-edit" onClick={() => setEditing(true)} className="emp-link text-sm">Edit</button>}</div>
        {!editing ? (
          <div className="mt-2 text-sm space-y-1.5">
            <div>{p.phone || <span className="text-slate-400">No phone</span>}</div>
            <div>{[p.address_line1, p.city, p.province, p.postal_code].filter(Boolean).join(", ") || <span className="text-slate-400">No address</span>}</div>
            <div className="text-slate-500">Emergency: {[p.emergency_contact_name, p.emergency_contact_relationship, p.emergency_contact_phone].filter(Boolean).join(" · ") || "—"}</div>
          </div>
        ) : (
          <div className="mt-2">
            {CONTACT.map((k) => <label key={k} className="block mb-2"><span className="emp-label">{LABELS[k]}</span><input data-testid={`me-contact-${k}`} value={contact[k]} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} className={inp} /></label>)}
            <div className="flex gap-3 mt-4"><button data-testid="me-contact-save" disabled={busy} onClick={saveContact} className="emp-btn-sm">Save</button><button onClick={() => setEditing(false)} className="emp-btn-secondary">Cancel</button></div>
          </div>
        )}
      </Card>

      <Card>
        <Label>On file with your employer</Label>
        <div className="mt-2 grid grid-cols-2 gap-y-1.5 text-sm">
          {[["Email", p.email], ["Date of birth", p.date_of_birth], ["SIN", p.sin_masked], ["TD1 federal", p.td1_federal_claim], ["TD1 Ontario", p.td1_provincial_claim],
            ["Bank", p.banking.on_file ? `${p.banking.bank_name} · ${p.banking.institution} / ${p.banking.transit} / ${p.banking.account}` : "Not on file"]].map(([k, v]) => <div key={k} className="contents"><span className="text-slate-500">{k}</span><span className="text-right break-all" data-testid={`me-field-${k.toLowerCase().replace(/\W+/g, "-")}`}>{v ?? "—"}</span></div>)}
        </div>
      </Card>

      <Card data-testid="me-request-card">
        <Label>Request a change</Label>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">Name, email, date of birth, TD1 claims and banking changes are reviewed by your employer before they take effect.</p>
        {pending ? <p data-testid="me-request-pending" className="text-sm font-medium text-amber-700 bg-amber-50 rounded-2xl px-4 py-3 mt-4">You have a request waiting for approval. You can send another once it's decided.</p> : (
          <div className="mt-3">
            {p.request_fields.map((k) => <label key={k} className="block mb-2"><span className="emp-label">{LABELS[k] || k}</span><input data-testid={`me-request-${k}`} value={changes[k] || ""} placeholder="leave blank to keep" onChange={(e) => setChanges({ ...changes, [k]: e.target.value })} className={inp} /></label>)}
            <label className="block mb-2"><span className="emp-label">Note to employer (optional)</span><textarea data-testid="me-request-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inp} /></label>
            <button data-testid="me-request-submit" disabled={busy} onClick={submitRequest} className="emp-btn-sm mt-2">Send request</button>
          </div>
        )}
      </Card>

      {reqs.length > 0 && (
        <Card data-testid="me-request-history"><Label>History</Label>
          <div className="mt-2 divide-y divide-slate-100 text-sm">
            {reqs.map((r) => (
              <div key={r.request_id} className="py-2.5">
                <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{fmtDateTime(r.created_at)}</span><span className={`emp-badge ${STATUS_CLS[r.status] || ""}`}>{r.status}</span></div>
                <div className="mt-1">{Object.entries(r.changes).map(([k, c]) => <div key={k}>{LABELS[k] || k}: <span className="text-slate-500 line-through">{k.startsWith("bank_") ? "•••" : c.from || "—"}</span> → <span className="font-medium">{k.startsWith("bank_") ? "•••" + String(c.to).slice(-3) : c.to}</span></div>)}</div>
                {r.decision_note && <div className="text-xs text-slate-500 mt-1">Employer: {r.decision_note}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
